import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { HeaderHeight, PageHeaderHeight } from '../Constants';
import AddNewRoleModal from './_dashboard/smartlock/AddNewRoleModal';
import ConfirmAlertDialog from './ConfirmDialog';
import NoDataAvailable from './_dashboard/smartlock/NoDataAvailable';
import { DeleteRoleById, GetAllRoles } from '../network/Role';

const LoaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50px' // Adjust height as needed
});

const useStyles = makeStyles((theme) => ({
  tableCellStyle: {
    fontSize: {
      xs: '12px',
      md: '14px'
    },
    fontWeight: 700,
    padding: {
      xs: '8px',
      md: '16px'
    },
    textWrap: 'nowrap',
    backgroundColor: '#FFFFFF'
  },
  tablePaper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF'
  },
  tableContainer: {
    flexGrow: 1,
    height: `calc(100vh - ${HeaderHeight + PageHeaderHeight + 56 + 40 + 50 + 18}px)`,
    overflow: 'auto'
  }
}));

const PageHeader = styled('Box')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  flexDirection: 'row !important',
  height: PageHeaderHeight
}));

export default function RoleManagement() {
  const classes = useStyles();
  const [isAssignAddModal, setIsAssignAddModal] = useState(false);
  const [openConfirmAlertDialog, setOpenConfirmAlertDialog] = useState(false);
  const [loader, setLoader] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [selectedRoleData, setSelectedRoleData] = useState({});
  const [page, setPage] = useState(1);
  const [confirmAlertDialogMetas, setConfirmAlertDialogMetas] = useState({
    message: '',
    title: '',
    positiveBtnLable: '',
    negativeBtnLabel: ''
  });

  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    vertical: 'top',
    horizontal: 'right',
    msg: '',
    variant: 'filled',
    severity: ''
  });
  const vertical = 'bottom';
  const horizontal = 'left';

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMetas({ ...alertMetas, snackBarOpened: false });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRoleMoreMenuClick = (action, data) => {
    switch (action) {
      case 'edit_role': {
        setSelectedRoleData(data);
        setIsAssignAddModal(true);
        break;
      }
      case 'delete_role': {
        setSelectedRoleData(data);
        setConfirmAlertDialogMetas({
          ...confirmAlertDialogMetas,
          message: 'Are you sure want to delete this Role ?',
          title: 'Delete',
          positiveBtnLable: 'Confirm',
          negativeBtnLabel: 'Cancel'
        });
        setOpenConfirmAlertDialog(true);
        break;
      }
      default:
        return '';
    }
  };

  const deleteRoleById = async (id) => {
    try {
      const res = await DeleteRoleById(id);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: 'Role Deleted !'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        onAddRoleModalaction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  const getRoleList = async (page) => {
    setLoader(true);
    try {
      const res = await GetAllRoles(page);
      if (res) {
        setRoleList(res?.data);
      }
    } catch (error) {
      console.log(error, 'err');
    } finally {
      setLoader(false);
    }
  };

  const onAddRoleModalaction = () => {
    getRoleList(page);
  };

  useEffect(() => {
    getRoleList(page);
  }, [page]);

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={alertMetas?.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert variant={alertMetas?.variant} severity={alertMetas?.severity}>
          {alertMetas?.msg}
        </Alert>
      </Snackbar>
      <Card sx={{ width: '100%', padding: '10px' }}>
        <Box>
          <PageHeader>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setIsAssignAddModal(true)}
              endIcon={<PersonAddAltOutlinedIcon />}
              sx={{ backgroundColor: '#FFFFFF' }}
            >
              Add
            </Button>
          </PageHeader>
          <Box sx={{ width: '100%', typography: 'body1', borderTop: '1px solid #EBEBEB' }}>
            <Paper className={classes.tablePaper}>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        S.No
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Role Name
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} minWidth={500}>
                        Description
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roleList?.data?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <NoDataAvailable />
                        </TableCell>
                      </TableRow>
                    )}
                    {loader ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <LoaderContainer>
                            <CircularProgress size={30} />
                          </LoaderContainer>
                        </TableCell>
                      </TableRow>
                    ) : (
                      roleList?.data?.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="left"
                          >
                            {(page - 1) * 10 + (index + 1)}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              textTransform: 'capitalize',
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                          >
                            {item.roleName}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              textTransform: 'capitalize',
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                          >
                            {item?.description ? item?.description : 'No description availbale'}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="left"
                          >
                            <Tooltip title="Edit" placement="top">
                              <IconButton
                                onClick={() => handleRoleMoreMenuClick('edit_role', item)}
                              >
                                <BorderColorOutlinedIcon
                                  sx={{ fontSize: { xs: '18px', md: '24px' } }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" placement="top">
                              <IconButton
                                onClick={() => handleRoleMoreMenuClick('delete_role', item)}
                              >
                                <DeleteOutlineOutlinedIcon
                                  sx={{ color: '#C30A0A', fontSize: { xs: '18px', md: '24px' } }}
                                />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {roleList?.data?.length !== 0 && (
              <Stack
                sx={{
                  marginTop: '10px',
                  marginBottom: '10px'
                }}
              >
                <Pagination
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                  count={roleList?.totalPages}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  page={page}
                  total={roleList?.total}
                />
              </Stack>
            )}
          </Box>
        </Box>
      </Card>
      {isAssignAddModal && (
        <AddNewRoleModal
          openAssignedModal={isAssignAddModal}
          handleClose={() => setIsAssignAddModal(false)}
          onAction={() => onAddRoleModalaction()}
          setAlertMetas={setAlertMetas}
          alertMetas={alertMetas}
          selectedRoleData={selectedRoleData}
          setSelectedRoleData={setSelectedRoleData}
        />
      )}
      <ConfirmAlertDialog
        openConfirmAlertDialog={openConfirmAlertDialog}
        message={confirmAlertDialogMetas?.message}
        title={confirmAlertDialogMetas?.title}
        positiveBtnLable={confirmAlertDialogMetas?.positiveBtnLable}
        negativeBtnLable={confirmAlertDialogMetas?.negativeBtnLabel}
        handleClose={() => setOpenConfirmAlertDialog(false)}
        handlePositiveClose={() => {
          deleteRoleById(selectedRoleData?.id);
          setOpenConfirmAlertDialog(false);
        }}
        handleNegativeClose={() => setOpenConfirmAlertDialog(false)}
      />
    </Box>
  );
}
